import * as React from 'react';
import { path, pathOr } from 'ramda';
import { useIsDesktop } from '@cian/valuation-utils-component';
import { useDispatch, useSelector } from 'react-redux';

import { IBaseQuestionProps, TAnswersState } from '../FormContainer/types/internal';
import { QuestionLayoutContainer } from '../QuestionLayoutContainer';
import { useFormTracking } from '../../utils/useFormTracking';
import { Textarea } from '../../components/Textarea';
import { useSimpleForm } from '../../utils/useSimpleForm';
import { pickOnlyMeaning } from '../../utils/pickOnlyMeaning';
import { maxStrLength, requiredValidator, validatorsPipe } from '../../utils/validators';
import { EAnswerField } from '../../repositories/lead-realtor-form/entities/schema/ConditionSchema';
import { useAvailableRealtorChoice } from './utils/useAvailableRealtorChoice';
import { SpecificWrapper } from '../../components/SpecificWrapper/SpecificWrapper';
import { ModalLoader } from '../../components/ModalLoader';
import { EQuestionCode } from '../../types/form';
import { AgreementBlockContainer } from '../AgreementBlockContainer';
import { selectUserAgreement } from '../../selectors/selectUserMarketingAgreement';
import { setMarketingAgreement } from '../../slices';

export const TextareaCustomQuestionContainer: React.FC<React.PropsWithChildren<IBaseQuestionProps>> = props => {
  const {
    question: { answerField, placeholder, title, required, code, helpText },
    answersState,
    setNextStep,
    setAnswersState,
  } = props;

  const dispatch = useDispatch();
  const isDesktop = useIsDesktop();
  const isMarketingAgreement = useSelector(selectUserAgreement);
  const { trackSubmit, trackChange } = useFormTracking();
  const { isAvailableChoice, isFetching } = useAvailableRealtorChoice(answersState);

  const handleChangeMarketingAgreement = () => {
    dispatch(setMarketingAgreement(!isMarketingAgreement));
  };

  const {
    handleSubmit,
    handleChange: onChange,
    values,
    errors,
    isSubmitDisabled,
  } = useSimpleForm({
    onSubmit(values) {
      setAnswersState(prevState => {
        const nextAnswersState: TAnswersState = {
          ...prevState,
          [answerField]: {
            answerField,
            value: values[answerField],
          },
          isAvailableRealtorChoice: {
            answerField: EAnswerField.IsAvailableRealtorChoice,
            value: String(isAvailableChoice),
          },
          wantToChooseRealtor: {
            answerField: EAnswerField.WantToChooseRealtor,
            value: 'no',
          },
        };

        setNextStep(nextAnswersState);
        trackSubmit({
          isRequired: required,
          field: answerField,
          value: null,
        });

        return nextAnswersState;
      });
    },
    validators: {
      [answerField]: validatorsPipe([
        [requiredValidator(required), 'Введите значение'],
        [maxStrLength(2000), 'Поле не должно быть больше 2000 символов'],
      ]),
    },
    initialValues: pickOnlyMeaning({
      [answerField]: path([answerField, 'value'], answersState),
    }),
    required,
  });

  const handleChange = React.useCallback<React.ChangeEventHandler<HTMLTextAreaElement>>(
    e => {
      trackChange();

      onChange(answerField, e.target.value);
    },
    [onChange, answerField, trackChange],
  );

  if (isFetching) {
    return (
      <SpecificWrapper>
        <ModalLoader />
      </SpecificWrapper>
    );
  }

  return (
    <>
      <QuestionLayoutContainer
        title={title}
        onSubmit={handleSubmit}
        disableSubmit={isSubmitDisabled}
        buttonText={code === EQuestionCode.Comment ? 'Отправить заявку' : props.submitButtonText}
        description={helpText?.text}
        error={errors[answerField] || props.error}
        showButtonPreloader={props.showButtonPreloader}
        buttonFullWidth={!isDesktop}
      >
        <Textarea
          autoFocus
          placeholder={placeholder || ''}
          onChange={handleChange}
          value={pathOr('', [answerField], values)}
          rows={4}
        />
      </QuestionLayoutContainer>
      {code === EQuestionCode.Comment && (
        <AgreementBlockContainer
          isChecked={isMarketingAgreement}
          toggleCheckbox={handleChangeMarketingAgreement}
          buttonText="Отправить заявку"
        />
      )}
    </>
  );
};

TextareaCustomQuestionContainer.displayName = 'TextareaCustomQuestionContainer';
